import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-budget-total-costs-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './budget-total-costs-chart.component.html',
  styleUrl: './budget-total-costs-chart.component.css',
})
export class BudgetTotalCostsChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;
  labels: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummyTotalCosts = [120, 200, 140, 150, 160, 220, 180, 190, 260, 210, 220, 150];
  dummyForecastedStartMonth = 6;

  totalCostsLabel: string = "Total Costs (EUR)";

  @Input() totalCosts: (number | null)[] = [120, 200, 140, 150, 160, 220, 180, 190, 260, 210, 220, 150];
  @Input() forecastedStartMonth: number | null = null;

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart()
  }

  ngOnInit(): void {
    this.updateChart();

    combineLatest([
      this.translocoService.selectTranslate('dashboard.total_costs_label'),
    ]).subscribe(([totalCostsLabel]) => {
      this.totalCostsLabel = totalCostsLabel;
    });

    const monthKeys = [
      'shared.months.jan',
      'shared.months.feb',
      'shared.months.mar',
      'shared.months.apr',
      'shared.months.may',
      'shared.months.jun',
      'shared.months.jul',
      'shared.months.aug',
      'shared.months.sep',
      'shared.months.oct',
      'shared.months.nov',
      'shared.months.dec',
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.labels = translations;
      this.updateChart();
    });
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.totalCosts = this.dummyTotalCosts;
      this.forecastedStartMonth = this.dummyForecastedStartMonth;
    }

    Chart.register(ChartDataLabels);
    this.data = {
      labels: this.labels,
      datasets: [
        {
          label: this.totalCostsLabel,
          data: this.totalCosts,
          borderColor: '#A9BE92',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 5,
          pointBackgroundColor: '#A9BE92',
          segment: {
            borderDash: (ctx: any) => {
              const index = ctx.p0DataIndex;
              if (this.forecastedStartMonth === null) {
                return undefined;
              }
              return index >= this.forecastedStartMonth ? [5, 5] : undefined;
            },
          },
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: this.isMobile ? 0 : 80,
        }
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          align: 'end',
          anchor: 'center',
          clamp: true,
          clip: false,
          display: 'auto',
          formatter: (_value: any, context: any) => {
            return `${this.decimalPipe.transform(_value, '1.0-0') || _value}€`;
          },
          color: "#000000",
          font: {
            size: 12,
            weight: 'normal',
            family: 'Gilroy',
          },
        },
        tooltip: {
          callbacks: {
            label: this.formatTooltipLabel.bind(this)
          }
        }

      },
      scales: {
        x: {
          display: false,
          offset: true,
        },
        y: {
          display: false,
          beginAtZero: true,
        },
      },
    };
  }

  formatTooltipLabel(tooltipItem: any) {
    let label = tooltipItem.dataset.label || '';
    let value = tooltipItem.raw || 0;
    return `${label}: ${this.decimalPipe.transform(value, '1.0-0') || value}`;
  }

}
